import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  Bot,
  FolderKanban,
  MonitorPlay,
  Users,
  Share2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import FeatureShowcase from './FeatureShowcase';

const features = [
  {
    title: 'Visual Editor',
    description: 'A powerful web IDE for building data apps with real-time code execution and instant feedback.',
    icon: LayoutDashboard,
    href: '#',
    color: 'text-cyan-500',
  },
  {
    title: 'Agent',
    description: 'AI-powered assistant that helps you analyze data and create visualizations using natural language.',
    icon: Bot,
    href: '#',
    color: 'text-yellow-500',
  },
  {
    title: 'Projects Dashboard',
    description: 'Central hub to manage all your projects, with easy access to files, collaborators, and settings.',
    icon: FolderKanban,
    href: '#',
    color: 'text-green-500',
  },
  {
    title: 'Live Previews',
    description: 'See your changes instantly as you code, with automatic updates and real-time rendering.',
    icon: MonitorPlay,
    href: '#',
    color: 'text-red-500',
  },
  {
    title: 'Team Collaboration',
    description: 'Work together seamlessly with real-time editing, comments, and shared project access.',
    icon: Users,
    href: '#',
    color: 'text-purple-500',
  },
  {
    title: 'Export & Share',
    description: 'Deploy your app as a static website or share it with others through a simple link.',
    icon: Share2,
    href: '#',
    color: 'text-orange-500',
  },
];

const FeaturesSection: React.FC = () => {
  const [selectedFeatureIndex, setSelectedFeatureIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setSelectedFeatureIndex((current) => (current + 1) % features.length);
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-24 bg-background border border-[#e0e0e0]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className={cn(
            "text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl",
            "bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent"
          )}>
            Code, Collaborate, Deploy
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-muted-foreground">
            A complete development platform with AI assistance, real-time collaboration, and instant deployment
          </p>
        </div>

        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Features List */}
            <div className="w-full lg:w-1/3">
              <div className="h-[700px] rounded-xl border flex flex-col">
                {features.map((feature, index) => {
                  const Icon = feature.icon;
                  return (
                    <button
                      key={index}
                      onClick={() => setSelectedFeatureIndex(index)}
                      className={cn(
                        "group flex flex-col w-full text-left flex-1",
                        "p-3 transition-all duration-200 border-b last:border-b-0",
                        selectedFeatureIndex === index ? 'bg-muted/50' : 'hover:bg-muted/50'
                      )}
                    >
                      <div className="flex items-center gap-3 mb-1.5">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg border bg-background">
                          <Icon className={cn("h-4 w-4", feature.color, "group-hover:opacity-80 transition-colors")} />
                        </div>
                        <h3 className="text-base font-semibold text-foreground">{feature.title}</h3>
                      </div>
                      <p className="text-sm leading-5 text-muted-foreground">
                        {feature.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Interactive Demo */}
            <div className="w-full lg:w-2/3">
              <FeatureShowcase selectedFeatureIndex={selectedFeatureIndex} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
